"use client";

import React, { useRef, useState } from "react";
import styles from "./DragDropFileinput.module.scss";
import { Controller, ControllerRenderProps } from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import toast from "@/lib/toast";
import UploadIcon from "@/components/SVGComponents/UploadIcon";
import { addBaseUrl, combineClasses } from "@/lib/utils";
import {
  DragDropFileControlProps,
  WithLabelAndErrorComponent,
} from "@/types/formControls";

type FileValidationOptions = {
  accept?: string; // e.g. "image/*,.pdf"
  maxSize?: number; // in bytes
};

interface ExtendedFileControlProps extends DragDropFileControlProps {
  maxFiles?: number; // NEW optional prop
  height?: string | number;
}

const DragDropFileInput: React.FC<ExtendedFileControlProps> = ({
  control,
  validations,
  name,
  placeholder = "Select files...",
  isDisabled,
  maxSize,
  onFileDrop,
  accept,
  maxFiles, // new prop
  borderColor,
  title,
  subtitle,
  icon,
  height,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileValidation = (
    files: File[],
    { accept, maxSize }: FileValidationOptions
  ): File[] => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    const acceptedTypes = accept?.split(",").map((rule) => rule.trim());

    files.forEach((file) => {
      if (maxSize && file.size > maxSize) {
        invalidFiles.push(`${file.name} (too large)`);
        return;
      }

      if (acceptedTypes?.length) {
        const fileExt = "." + file.name?.split(".").pop()?.toLowerCase();
        const mimeType = file.type.toLowerCase();

        const isValid = acceptedTypes.some((rule) => {
          if (rule.startsWith(".")) {
            return fileExt === rule.toLowerCase();
          }
          if (rule.endsWith("/*")) {
            const typeCategory = rule?.split("/")[0];
            return mimeType.startsWith(typeCategory + "/");
          }
          return mimeType === rule.toLowerCase();
        });

        if (!isValid) {
          invalidFiles.push(file.name);
          return;
        }
      }

      validFiles.push(file);
    });

    if (invalidFiles.length) {
      toast.error(`These files are not allowed: ${invalidFiles.join(", ")}`);
    }

    return validFiles;
  };

  const renderInput = (field: ControllerRenderProps<any, string>) => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      let validFiles = handleFileValidation(files, { accept, maxSize });

      let newFiles = [...(field.value || []), ...validFiles];

      // ✅ Enforce maxFiles
      if (maxFiles && newFiles.length > maxFiles) {
        toast.error(`You can only upload up to ${maxFiles} files`);
        newFiles = newFiles.slice(0, maxFiles);
      }

      field.onChange(newFiles);
      onFileDrop?.(newFiles);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      let validFiles = handleFileValidation(files, { accept, maxSize });

      let newFiles = [...(field.value || []), ...validFiles];

      // ✅ Enforce maxFiles
      if (maxFiles && newFiles.length > maxFiles) {
        toast.error(`You can only upload up to ${maxFiles} files`);
        newFiles = newFiles.slice(0, maxFiles);
      }

      field.onChange(newFiles);
      onFileDrop?.(newFiles);
    };

    const handleRemoveFile = (index: number) => {
      const newFiles = field.value.filter((_: File, i: number) => i !== index);
      field.onChange(newFiles);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    const hasFiles = field.value && field.value.length > 0;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1, }}>
        <input
          ref={inputRef}
          type="file"
          id={name}
          name={name}
          disabled={isDisabled}
          accept={accept}
          className={combineClasses(styles.inputField, styles.hiddenFileInput)}
          onChange={handleFileChange}
          multiple
        />

        {hasFiles ? (
          <div
            className={maxFiles === 1 ? styles.previewGridLarge : styles.previewGrid}
            style={maxFiles === 1 && height ? { height: height, minHeight: height } : undefined}
          >
            {field.value.map((file: File | string, index: number) => {
              const isString = typeof file === "string";
              const url = isString ? addBaseUrl(file) : URL.createObjectURL(file);
              const fileName = isString ? String(file).split("/").pop() : file.name;
              const isImage = /\.(jpe?g|png|gif|webp|svg)$/i.test(fileName || "");
              const isPdf = /\.pdf$/i.test(fileName || "");

              return (
                <div
                  key={index}
                  className={styles.previewItem}
                  style={{
                    borderColor: maxFiles === 1 ? (borderColor || undefined) : undefined,
                    backgroundColor: (maxFiles === 1 && borderColor) ? `color-mix(in srgb, ${borderColor} 10%, transparent)` : undefined,
                    height: maxFiles === 1 && height ? height : undefined,
                    minHeight: maxFiles === 1 && height ? height : undefined
                  }}
                >
                  {isImage ? (
                    <img src={url} alt={fileName} className={styles.previewImage} />
                  ) : isPdf ? (
                    <div className={styles.pdfPreview}>
                      <embed src={url} type="application/pdf" width="100%" height="100px" />
                      <p className={styles.fileName}>{fileName}</p>
                    </div>
                  ) : (
                    <div className={styles.filePreview}>
                      <p className={styles.fileName}>{fileName}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className={styles.removeChip}
                  >
                    &times;
                  </button>
                </div>
              );
            })}

            {/* Add new file button (only if not maxed out) */}
            {(!maxFiles || field.value.length < maxFiles) && (
              <div
                className={styles.previewItem}
                onClick={() => inputRef.current?.click()}
              >
                <UploadIcon style={{ width: "32px", height: "32px" }} />
                <p className={styles.addText}>Add</p>
              </div>
            )}
          </div>
        ) : (
          <div
            className={`${styles.dragDropWrapper} ${isDragging ? styles.dragActive : ""}`}
            style={{
              borderColor: borderColor || undefined,
              backgroundColor: borderColor ? `color-mix(in srgb, ${borderColor} 10%, transparent)` : undefined,
              height: height || undefined,
              minHeight: height || undefined
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            {icon ? icon : <UploadIcon style={{ width: "32px", height: "32px", color: borderColor || "#3B82F6" }} />}
            <div className={styles.dropZoneContent}>
              <div className={styles.customTitle}>
                {title !== undefined ? title : (
                  <span>
                    <span className={styles.clickHere}>Click here</span> to upload or drop files here
                  </span>
                )}
              </div>
              {subtitle !== undefined ? (
                subtitle && <div className={styles.customSubtitle}>{subtitle}</div>
              ) : (
                <div className={styles.customSubtitle}>Supported formats: JPEG, PNG, PDF</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Controller
      control={control}
      rules={validations}
      name={name}
      render={({ field }) => renderInput(field)}
    />
  );
};

const DragDropFileInputWithLabelAndError = withLabelAndError(
  DragDropFileInput as WithLabelAndErrorComponent
);

export default DragDropFileInputWithLabelAndError;
export { DragDropFileInput };
